import type { ContentRegistry } from '@/core/content/registry';
import { advanceNeighborhood } from '@/core/domain/neighborhood';
import { openBox, placeItem, rotateItem, storeItem, type HandlerEnv } from '@/core/domain/placement/handlers';
import { applyWallFloor, buyItem, buyStock, buyWallFloor, markCatalogSeen } from '@/core/domain/economy';
import { finishMiniGame } from '@/core/domain/minigames';
import { displayProduct, moveProduct, toggleShopOpen, undisplayProduct } from '@/core/domain/shop';
import { advanceDialogue, petCat, talkToNpc } from '@/core/domain/dialogue';
import { fulfillOrder, skipOrder } from '@/core/domain/orders';
import { cancelShoot, endShoot, finishShoot, skipShootRequest, startShoot } from '@/core/domain/studio';
import { claimMail } from '@/core/domain/mailbox';
import { claimReward, markChapterSeen, swapSuggestion } from '@/core/domain/progression';
import { withRewardLedger } from '@/core/domain/rewards';
import { payUnlock } from '@/core/domain/unlocks';
import { deletePhoto, takePhoto } from '@/core/domain/photos';
import { equipTitle, setAvatar } from '@/core/domain/avatar';
import {
  advanceTime,
  bootSession,
  navigateScene,
  toggleFavorite,
  updateLastSession,
  updateSettings,
} from '@/core/domain/session';
import type { DomainEvent } from '@/core/events/types';
import type { LedgerEntry } from '@/core/persistence/db';
import type { GameSave } from '@/core/persistence/schema/v1';
import { structuralShare } from '@/core/util/structuralShare';
import {
  CRITICAL_COMMANDS,
  EPHEMERAL_COMMANDS,
  type Command,
  type CommandOf,
  type CommandResult,
  type CommandType,
} from './commands';

type Handler<T extends CommandType> = (draft: GameSave, cmd: CommandOf<T>, env: HandlerEnv) => CommandResult;

const HANDLERS: { [T in CommandType]: Handler<T> } = {
  advanceNeighborhood,
  placeItem,
  rotateItem,
  storeItem,
  openBox,
  finishMiniGame,
  updateSettings,
  toggleFavorite,
  markCatalogSeen,
  navigateScene,
  updateLastSession,
  advanceTime,
  bootSession,
  payUnlock,
  claimReward,
  swapSuggestion,
  markChapterSeen,
  talkToNpc,
  petCat,
  fulfillOrder,
  skipOrder,
  startShoot,
  cancelShoot,
  finishShoot,
  endShoot,
  skipShootRequest,
  claimMail,
  advanceDialogue,
  buyItem,
  buyStock,
  buyWallFloor,
  applyWallFloor,
  displayProduct,
  undisplayProduct,
  moveProduct,
  toggleShopOpen,
  takePhoto,
  deletePhoto,
  setAvatar,
  equipTitle,
};

/**
 * Post-processing system run after every successful command, in a FIXED order (content-schemas §13.3):
 * stats → coziness → friendship → achievements → suggestions → unlocks → rewards. Each returns new events,
 * which are fed to the next round until nothing new happens.
 */
export type DomainSystem = (
  draft: GameSave,
  events: readonly DomainEvent[],
  env: HandlerEnv,
) => DomainEvent[];

export interface DispatcherOptions {
  content: ContentRegistry;
  getState: () => GameSave;
  setState: (next: GameSave) => void;
  emit: (events: readonly DomainEvent[]) => void;
  markDirty: (priority: 'normal' | 'critical') => void;
  now: () => number;
  systems?: readonly DomainSystem[];
  /** Ledger rows of a committed command (persisted with the next save). */
  onLedger?: (entries: readonly LedgerEntry[]) => void;
}

export type Dispatch = (cmd: Command, origin?: HandlerEnv['origin']) => CommandResult;

const MAX_SYSTEM_ROUNDS = 8;

/** Repeatable money commands carry a caller-made id; a retry with the same id is a no-op (§11.5). */
function txIdOf(cmd: Command): string | undefined {
  return 'txId' in cmd ? cmd.txId : undefined;
}

/** Pure-ish pipeline: clone → handler → systems → commit → emit → schedule save. Failures change nothing. */
export function createDispatcher(opts: DispatcherOptions): Dispatch {
  const systems = opts.systems ?? [];
  return (cmd, origin = 'player') => {
    const txId = txIdOf(cmd);
    const before = opts.getState();
    if (txId && before.txLog.recent.some((t) => t.txId === txId))
      return { ok: true, events: [], replayed: true };
    const env: HandlerEnv = { content: opts.content, nowWallMs: opts.now(), origin, ledger: [] };
    const draft = structuredClone(before);
    const handler = HANDLERS[cmd.type] as Handler<typeof cmd.type>;
    const ran = withRewardLedger(env.ledger, env.nowWallMs, () => {
      const result = handler(draft, cmd, env);
      // Failures and idempotent replays change nothing.
      if (!result.ok || result.replayed) return { result, all: [] };
      const all = [...result.events];
      let fresh: readonly DomainEvent[] = result.events;
      for (let round = 0; fresh.length > 0 && systems.length > 0; round++) {
        if (round >= MAX_SYSTEM_ROUNDS) throw new Error('Domain systems did not settle');
        const produced: DomainEvent[] = [];
        for (const system of systems) produced.push(...system(draft, fresh, env));
        all.push(...produced);
        fresh = produced;
      }
      return { result, all };
    });
    const { result, all } = ran;
    if (!result.ok || result.replayed) return result;

    if (txId) {
      draft.txLog.recent = [...draft.txLog.recent, { txId, atPlayMs: draft.clock.playTimeMs }].slice(
        -opts.content.balance.save.txRingSize,
      );
    }
    // Unchanged slices keep their identity; the root is always new so every commit still notifies.
    for (const key of Object.keys(draft) as (keyof GameSave)[])
      (draft as Record<string, unknown>)[key] = structuralShare(before[key], draft[key]);
    opts.setState(draft);
    if (env.ledger && env.ledger.length > 0) opts.onLedger?.(env.ledger);
    if (all.length > 0) opts.emit(all);
    // Time ticks only need saving when something happened (a sale, an order…).
    const tickWithoutNews = cmd.type === 'advanceTime' && all.length === 0;
    if (!EPHEMERAL_COMMANDS.has(cmd.type) && !tickWithoutNews) {
      opts.markDirty(CRITICAL_COMMANDS.has(cmd.type) ? 'critical' : 'normal');
    }
    return { ok: true, events: all };
  };
}
